import { Select, Switch } from '@neo4j-ndl/react';
import { QUERY_STATUS } from '@nx/state';
import React from 'react';

import { InformationIconWithTooltip } from '../../shared/components';

interface QueryLogEntryFilterSelectorProps {
  label: string;
  disabled?: boolean;
  options: { label: string; value: QUERY_STATUS }[];
  onSelection: (selected: QUERY_STATUS) => void;
  additionalClasses?: string;
  defaultValue: QUERY_STATUS;
}

export const QueryLogEntryFilterSelector = ({
  label,
  disabled = false,
  options,
  onSelection,
  additionalClasses,
  defaultValue,
}: QueryLogEntryFilterSelectorProps) => {
  return (
    <Select
      aria-label="Include queries"
      className={`mr-4 w-fit ${additionalClasses}`}
      type="select"
      size="medium"
      label={label}
      isDisabled={disabled}
      selectProps={{
        delimiter: '=>',
        options,
        value: { value: defaultValue, label: options.find((o) => o.value === defaultValue)?.label ?? '' },
        menuPosition: 'fixed',
        onChange: (e) => {
          if (e) {
            onSelection(e.value);
          }
        },
      }}
    />
  );
};

interface QueryLogFiltersProps {
  filterEventType: QUERY_STATUS;
  setFilterEventType?: (value: QUERY_STATUS) => void;
  readOnly?: boolean;
}

export const QueryLogFilters = ({ filterEventType, setFilterEventType, readOnly = false }: QueryLogFiltersProps) => {
  // Determine if we're in summary mode (filterEventType is QUERY_STATUS_SUMMARY)
  const isSummaryMode = filterEventType === QUERY_STATUS.QUERY_STATUS_SUMMARY;

  // The transformed event type for display purposes
  const transformedFilterEventType = isSummaryMode ? QUERY_STATUS.QUERY_STATUS_ALL : filterEventType;

  // Handle changes to the summary filter
  const handleSummaryChange = (newSummaryValue: boolean) => {
    if (!readOnly) {
      if (setFilterEventType) {
        if (newSummaryValue) {
          // If summary is enabled, set the event type to SUMMARY
          setFilterEventType(QUERY_STATUS.QUERY_STATUS_SUMMARY);
        } else if (isSummaryMode) {
          // If we're disabling summary and we were in summary mode, switch to ALL
          setFilterEventType(QUERY_STATUS.QUERY_STATUS_ALL);
        }
      }
    }
  };

  // Handle changes to the event type filter
  const handleEventTypeChange = (selected: QUERY_STATUS) => {
    if (setFilterEventType && !readOnly) {
      setFilterEventType(selected);
    }
  };

  return (
    <div>
      <div className="my-4 flex">
        <Switch
          isDisabled={readOnly || transformedFilterEventType !== QUERY_STATUS.QUERY_STATUS_ALL}
          className="self-center"
          ariaLabel="Remove start entries"
          label="Remove start entries"
          isChecked={transformedFilterEventType !== QUERY_STATUS.QUERY_STATUS_ALL || isSummaryMode}
          onChange={() => handleSummaryChange(!isSummaryMode)}
        />
        <InformationIconWithTooltip
          tooltip={
            transformedFilterEventType === QUERY_STATUS.QUERY_STATUS_ALL
              ? 'Each query produces one full log entry at the transaction start, and one at the end. Enable this option to remove the first log entry of each query.'
              : 'Whether a query is successful or failed is not known at the start, and start entries will therefore be removed.'
          }
          isPortaled={false}
        />
      </div>
      <QueryLogEntryFilterSelector
        disabled={readOnly}
        label="Include: "
        defaultValue={transformedFilterEventType}
        options={[
          { label: 'All queries', value: QUERY_STATUS.QUERY_STATUS_ALL },
          { label: 'Successful queries', value: QUERY_STATUS.QUERY_STATUS_SUCCESS },
          { label: 'Failed queries', value: QUERY_STATUS.QUERY_STATUS_ERROR },
        ]}
        onSelection={handleEventTypeChange}
      />
    </div>
  );
};
